import React, { useRef } from 'react';
import { Note, NoteRelevance } from '@shared/types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PencilIcon, TrashIcon, Paperclip, Upload, Download, Image, File } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const getRelevanceBadgeVariant = (relevance: NoteRelevance) => {
  switch (relevance) {
    case 'destacada': return 'destructive';
    case 'importante': return 'warning';
    default: return 'secondary';
  }
};

const getFileIcon = (mimeType: string) => {
  if (mimeType.startsWith('image/')) return Image;
  return File;
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface ViewNoteDialogProps {
  note: Note | null;
  onClose: () => void;
  onEdit: (note: Note) => void;
  onFileUpload: (noteId: string, file: globalThis.File) => void;
  onDeleteAttachment: (noteId: string, index: number) => void;
  isUploading: boolean;
}

export function ViewNoteDialog({ note, onClose, onEdit, onFileUpload, onDeleteAttachment, isUploading }: ViewNoteDialogProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <Dialog open={!!note} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
        {note && (
          <>
            <DialogHeader>
              <div className="flex items-start justify-between gap-4">
                <DialogTitle className="text-xl">{note.title}</DialogTitle>
                <Badge variant={getRelevanceBadgeVariant(note.relevance)} className="gap-1 capitalize shrink-0">
                  {note.relevance}
                </Badge>
              </div>
              <DialogDescription>
                Creada el {format(new Date(note.createdAt), 'PPP', { locale: es })}
                {note.updatedAt && note.updatedAt !== note.createdAt && (
                  <> · Actualizada el {format(new Date(note.updatedAt), 'PPP', { locale: es })}</>
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <div className="prose prose-sm dark:prose-invert max-w-none">
                <pre className="whitespace-pre-wrap font-sans text-foreground bg-transparent p-0 m-0">
                  {note.content}
                </pre>
              </div>

              {/* Attachments Section */}
              <div className="mt-6 border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    Adjuntos ({note.adjuntos?.length || 0})
                  </h4>
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      accept="image/*,text/*,.pdf,.md,.csv"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          onFileUpload(note.id, file);
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }
                      }}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {isUploading ? 'Subiendo...' : 'Adjuntar archivo'}
                    </Button>
                  </div>
                </div>

                {note.adjuntos && note.adjuntos.length > 0 ? (
                  <div className="space-y-2">
                    {note.adjuntos.map((adjunto, index) => {
                      const FileIcon = getFileIcon(adjunto.mimeType);
                      return (
                        <div
                          key={index}
                          className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="p-2 bg-primary/10 rounded shrink-0">
                              <FileIcon className="h-4 w-4 text-primary" />
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm truncate">{adjunto.nombre}</p>
                              <p className="text-xs text-muted-foreground">
                                {formatFileSize(adjunto.tamaño)} ·{' '}
                                {format(new Date(adjunto.fechaSubida), 'PP', { locale: es })}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button variant="ghost" size="icon" asChild title="Descargar">
                              <a href={adjunto.url} target="_blank" rel="noopener noreferrer" download>
                                <Download className="h-4 w-4" />
                              </a>
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => onDeleteAttachment(note.id, index)}
                              title="Eliminar adjunto"
                            >
                              <TrashIcon className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No hay archivos adjuntos. Puedes añadir imágenes, textos o PDFs.
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cerrar</Button>
              <Button onClick={() => { onEdit(note); onClose(); }}>
                <PencilIcon className="mr-2 h-4 w-4" /> Editar
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
